// Typen und Darstellungs-Helfer für die Analytics-Ansichten.
//
// WICHTIG: Hier wird NICHT gerechnet. Die Aggregation der Rohdaten passiert
// ausschließlich serverseitig in der Cloud Function `aggregate()` in
// parse/cloud/analytics-share.js. Diese Datei enthielt früher eine zweite,
// ungenutzte Kopie dieser Logik — sie wurde entfernt, weil Fehler dort
// scheinbar behoben werden konnten, ohne dass sich am Dashboard etwas ändert.
// Änderungen an den Zahlen gehören in die Cloud Function.

import dayjs from "dayjs";

export interface DailyCount {
  date: string;
  count: number;
}

export interface PageStat {
  pageName: string;
  views: number;
  avgDurationSec: number;
  avgDurationLabel: string;
}

export interface EventStat {
  eventType: string;
  label: string;
  count: number;
}

export interface BreakdownEntry {
  label: string;
  count: number;
  percent: number;
}

export type GroupMode = "day" | "month" | "year";

export interface AnalyticsData {
  // true, wenn die Abfrage am Zeilenlimit abgeschnitten wurde. Dann fehlen die
  // ältesten Aufrufe des Zeitraums und alle Zahlen sind zu niedrig.
  truncated?: boolean;
  truncatedLimit?: number;
  totalViews: number;
  uniqueVisitors: number;
  uniqueSessions: number;
  bounceRate: number;
  avgDuration: string;
  daily: DailyCount[];
  pages: PageStat[];
  events: EventStat[];
  devices: BreakdownEntry[];
  browsers: BreakdownEntry[];
  languages: BreakdownEntry[];
  initialLanguages: BreakdownEntry[];
  visitorLanguages: BreakdownEntry[];
}

export const GROUP_CONFIG: Record<GroupMode, { shortLabel: string }> = {
  day: { shortLabel: "Tag" },
  month: { shortLabel: "Monat" },
  year: { shortLabel: "Jahr" },
};

export function formatPeriodLabel(key: string, mode: GroupMode): string {
  const d = dayjs(key);
  if (!d.isValid()) return key;
  if (mode === "day") return d.format("DD.MM.YYYY");
  if (mode === "month") return d.format("MMMM YYYY");
  return d.format("YYYY");
}

export function formatPeriodShortLabel(key: string, mode: GroupMode): string {
  const d = dayjs(key);
  if (!d.isValid()) return key;
  if (mode === "day") return d.format("DD.MM.");
  if (mode === "month") return d.format("MM/YY");
  return d.format("YYYY");
}

export function toCsv(data: AnalyticsData, tenant: string): string {
  const lines: string[] = [];
  const { daily, pages, events } = data;
  lines.push("Analyse-Export");
  lines.push(`Stadtwerk;${tenant}`);
  lines.push(
    `Zeitraum;${daily[0]?.date || ""} bis ${daily[daily.length - 1]?.date || ""}`
  );
  lines.push("");
  lines.push("Zusammenfassung");
  lines.push(`Unique Besucher;${data.uniqueVisitors}`);
  lines.push(`Seitenaufrufe;${data.totalViews}`);
  lines.push(`Sitzungen;${data.uniqueSessions}`);
  lines.push(`Bounce Rate;${data.bounceRate}%`);
  lines.push(`Ø Sitzungsdauer;${data.avgDuration}`);
  lines.push("");
  lines.push("Seitenaufrufe pro Tag");
  lines.push("Datum;Aufrufe");
  for (const d of daily) lines.push(`${d.date};${d.count}`);
  lines.push("");
  lines.push("Seiten");
  lines.push("Seite;Aufrufe;Ø Verweildauer");
  for (const p of pages)
    lines.push(`${p.pageName};${p.views};${p.avgDurationLabel}`);
  if (events.length > 0) {
    lines.push("");
    lines.push("Events");
    lines.push("Typ;Name;Anzahl");
    for (const e of events) lines.push(`${e.eventType};${e.label};${e.count}`);
  }
  return lines.join("\n");
}

export function downloadCsv(csv: string, filename: string) {
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
